import { Link, useNavigate } from 'react-router-dom';
import { flushSync } from 'react-dom';
import { isOfflineEnabled, setOfflineEnabled } from '../../api/offline/isOffline';
import { useAuth } from '../../context/AuthContext';
import { loadOfflineDb, withOfflineDb } from '../../api/offline/offlineDb';

const PROFILE_OPTIONS = [
  { role: 'superadmin', label: 'SuperAdmin', to: '/superadmin/dashboard' },
  { role: 'admin', label: 'Admin', to: '/admin' },
  { role: 'directeur', label: 'Directeur', to: '/directeur' },
  { role: 'administratif', label: 'Administratif', to: '/administratif' },
  { role: 'prof', label: 'Prof', to: '/prof' },
  { role: 'parent', label: 'Parent', to: '/parent' },
  { role: 'eleve', label: 'Élève', to: '/eleve' },
];

function buildDemoUser(role) {
  const safeRole = String(role || 'superadmin');
  const baseId = {
    superadmin: 900,
    admin: 910,
    directeur: 920,
    administratif: 930,
    prof: 940,
    parent: 950,
    eleve: 960,
  }[safeRole] || 999;

  return {
    id: baseId,
    username: `${safeRole}_demo`,
    email: `${safeRole}@demo.schoolsense.local`,
    role: safeRole,
    first_name: 'Compte',
    last_name: 'Démo',
  };
}

export default function DemoModeBanner() {
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const offline = isOfflineEnabled();
  if (!offline) return null;

  const currentRole = user?.role || 'superadmin';

  const quitDemo = () => {
    setOfflineEnabled(false);
    try {
      localStorage.removeItem('token');
      localStorage.removeItem('refresh');
      localStorage.removeItem('user');
    } catch {}
    globalThis.location.assign('/login');
  };

  const switchProfile = (role) => {
    const opt = PROFILE_OPTIONS.find((o) => o.role === role) || PROFILE_OPTIONS[0];
    const demoUser = buildDemoUser(opt.role);
    const token = `offline-access-${Date.now()}`;

    // Ensure DB exists and keep /me/ consistent with the selected profile
    loadOfflineDb();
    withOfflineDb((db) => ({
      ...db,
      auth: { ...db.auth, currentUser: demoUser },
    }));

    try {
      localStorage.setItem('refresh', `offline-refresh-${Date.now()}`);
    } catch {}

    // Avoid a race where ProtectedRoute for the *current* page (e.g. /superadmin/*)
    // redirects to /403 when we swap roles. We hop to a public route first.
    navigate('/', { replace: true });

    const run = () => {
      flushSync(() => {
        login(demoUser, token);
      });
      navigate(opt.to, { replace: true });
    };

    if (typeof queueMicrotask === 'function') queueMicrotask(run);
    else setTimeout(run, 0);
  };

  return (
    <div className="sticky top-16 z-20 -mx-4 sm:-mx-6 mb-4 border-b border-orange-200/60 bg-white/70 backdrop-blur">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 py-2 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center rounded-full bg-orange-100 text-orange-800 text-xs font-semibold px-2.5 py-1">
            Mode démo
          </span>
          <span className="text-xs sm:text-sm text-gray-700">
            Site complet en données en dur (offline)
          </span>

          <div className="flex items-center gap-2 ml-0 sm:ml-3">
            <span className="text-xs text-gray-500">Profil :</span>
            <select
              value={currentRole}
              onChange={(e) => switchProfile(e.target.value)}
              className="text-xs sm:text-sm px-2.5 py-1.5 rounded-md border border-gray-200 bg-white hover:bg-gray-50"
              aria-label="Sélectionner un profil de démo"
            >
              {PROFILE_OPTIONS.map((o) => (
                <option key={o.role} value={o.role}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/superadmin/profil"
            className="px-3 py-1.5 rounded-md border border-gray-200 hover:bg-gray-50 text-xs sm:text-sm"
          >
            Paramètres démo
          </Link>
          <button
            type="button"
            onClick={quitDemo}
            className="px-3 py-1.5 rounded-md bg-red-600 hover:bg-red-700 text-white text-xs sm:text-sm font-medium"
          >
            Quitter la démo
          </button>
        </div>
      </div>
    </div>
  );
}
